import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import InputField from '../InputField/InputField';

import './JobSearchAutocomplete.scss';

const JobSearchAutocomplete = (props) => {
    const {
        value,
        placeholder,
        onChange: externalChangeHandler,
        hasError,
        disabled,
        className,
        onKeyPress,
        showClearIcon,
        icon,
        name,
    } = props;

    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [inputHeight, setInputHeight] = useState(43);
    const [hasFocus, setHasFocus] = useState(false);
    const wrapperRef = useRef(null);

    const getSuggestions = async () => {
        try {
            const results = await axios.get(`/jobs/autocomplete`, {
                params: { query: value },
            });
            const { data } = results;
            if (
                data?.query === value &&
                data.suggestions?.completionResults?.length > 0
            ) {
                const newSuggestions = data.suggestions.completionResults.map(
                    (completionResult) => completionResult.suggestion
                );
                setSuggestions(newSuggestions);
                setShowSuggestions(true);
            }
        } catch (error) {
            console.log(`Unable to get suggestions${error}`);
        }
    };

    useEffect(() => {
        const getInputHeight = () => {
            if (wrapperRef !== null && wrapperRef.current !== null) {
                const { height } = wrapperRef.current.getBoundingClientRect();
                setInputHeight(height);
            }
        };
        window.addEventListener('resize', getInputHeight);
        getInputHeight();
        return () => window.removeEventListener('resize', getInputHeight);
    }, []);

    useEffect(() => {
        if (value?.length > 0) {
            getSuggestions();
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, [value]);

    const handleSugestionClick = (selectedSuggestion) => {
        externalChangeHandler(selectedSuggestion);
        setShowSuggestions(false);
    };

    const matchingQueryPrefix = (query, text) => {
        let maxCommonString = '';
        for (let i = 1; i < query.length; i += 1) {
            const subQuery = query.slice(0, i);
            if (
                text.includes(subQuery) &&
                subQuery.length > maxCommonString.length
            ) {
                maxCommonString = subQuery;
            } else {
                break;
            }
        }
        return maxCommonString;
    };

    const renderHighlightedSuggestion = (suggestion) => {
        const normalizedSuggestion = suggestion.toLowerCase();
        const normalizedValue = value.toLowerCase();
        let suggestionAsHtml = '';

        // Try to match full query
        let matchIndex = normalizedSuggestion.indexOf(normalizedValue);
        let matchingPrefix = normalizedValue;

        if (matchIndex === -1) {
            // Try to match prefix
            matchingPrefix = matchingQueryPrefix(
                normalizedValue,
                normalizedSuggestion
            );
            matchIndex = normalizedSuggestion.indexOf(matchingPrefix);
        }

        if (matchIndex !== -1) {
            const boldText = suggestion.slice(
                matchIndex,
                matchIndex + matchingPrefix.length
            );
            const startText = suggestion.slice(0, matchIndex);
            const endText = suggestion.slice(
                matchIndex + matchingPrefix.length
            );
            suggestionAsHtml += `<p>${startText}<strong>${boldText}</strong>${endText}</p>`;
        } else {
            suggestionAsHtml += `<p>${suggestion}</p>`;
        }

        return (
            <div
                onClick={() => handleSugestionClick(suggestion)}
                onKeyPress={() => {}}
                role="button"
                aria-hidden="true"
                dangerouslySetInnerHTML={{
                    __html: suggestionAsHtml,
                }}
            />
        );
    };

    const renderSugestions = () => {
        if (suggestions.length > 0 && hasFocus && value !== '') {
            return (
                <div
                    className="search-suggestions-wrapper"
                    style={{
                        top: `${inputHeight}px`,
                    }}
                >
                    <ul>
                        {suggestions.map((suggestion) => (
                            <li
                                key={`sugg-${suggestion}`}
                                className="search-suggest"
                            >
                                {renderHighlightedSuggestion(suggestion)}
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }
        return null;
    };

    const handleInputClear = () => {
        externalChangeHandler('');
        setShowSuggestions(false);
    };

    const handleFocusLoss = () => {
        setTimeout(() => {
            setHasFocus(false);
            setShowSuggestions(false);
        }, 200);
    };

    const handleFocus = () => {
        setHasFocus(true);
        if (value !== '' && value !== null) {
            setShowSuggestions(true);
        }
    };

    const handleChange = (newValue) => {
        if (newValue !== '' && newValue !== null && hasFocus) {
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
        externalChangeHandler(newValue);
    };

    const getCloseButtonTopMargin = () => {
        const closeButtonHeight = 37;
        const marginTop = (inputHeight - closeButtonHeight) / 2;
        return `${marginTop - 1}px`;
    };

    return (
        <div
            className={`job-search-autocomplete ${className}`}
            ref={wrapperRef}
        >
            <InputField
                type="text"
                placeholder={placeholder}
                inputValue={value}
                onChange={handleChange}
                onKeyPress={onKeyPress}
                hasError={hasError}
                disabled={disabled}
                qaDataAttr="jobSearchInput"
                icon={icon}
                name={name}
                autocomplete="off"
                onBlur={handleFocusLoss}
                onFocus={handleFocus}
            />
            {showClearIcon && value && value !== '' && (
                <i
                    className="clear-search"
                    onClick={handleInputClear}
                    onKeyPress={() => {}}
                    role="button"
                    aria-hidden="true"
                    style={{
                        top: getCloseButtonTopMargin(),
                    }}
                >
                    &times;
                </i>
            )}
            {showSuggestions && renderSugestions()}
        </div>
    );
};

JobSearchAutocomplete.defaultProps = {
    value: '',
    placeholder: '',
    hasError: false,
    disabled: false,
    className: '',
    onKeyPress: null,
    showClearIcon: true,
    icon: '',
    onChange: () => {},
    name: '',
};

JobSearchAutocomplete.propTypes = {
    value: PropTypes.string,
    placeholder: PropTypes.string,
    hasError: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    onKeyPress: PropTypes.func,
    showClearIcon: PropTypes.bool,
    icon: PropTypes.string,
    onChange: PropTypes.func,
    name: PropTypes.string,
};

export default JobSearchAutocomplete;
